import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, Icon, stitchOutlineBtn, stitchSectionLabel, stitchTooltipSlotProps } from '../ProjectOverview/parts/stitch';

export const bestandsIcoon = (naam = '') => {
  const ext = naam.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls'].includes(ext)) return 'table_view';
  if (['pptx', 'ppt'].includes(ext)) return 'co_present';
  if (['docx', 'doc'].includes(ext)) return 'description';
  if (ext === 'pdf') return 'picture_as_pdf';
  return 'draft';
};

/**
 * Documenten van deze fase: het íngevulde document (concept, script, draaiboek)
 * staat voorop; de templates zijn alleen het startpunt en verdwijnen achter een
 * linkje zodra er een echt document is geüpload.
 */
function FaseTemplates({ fase, projectRecId, projectNumber }) {
  const [templates, setTemplates] = useState(null);
  const [documenten, setDocumenten] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toonTemplates, setToonTemplates] = useState(false);
  const templateUploadRef = useRef(null);
  const docUploadRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const laad = useCallback(async () => {
    try {
      const [tr, dr] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/flow-templates?fase=${encodeURIComponent(fase)}`),
        projectRecId
          ? authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents?fase=${encodeURIComponent(fase)}`)
          : Promise.resolve(null),
      ]);
      setTemplates(tr?.ok ? (await tr.json())?.templates || [] : []);
      setDocumenten(dr?.ok ? (await dr.json())?.documenten || [] : []);
    } catch { setTemplates([]); setDocumenten([]); }
  }, [fase, projectRecId]);
  useEffect(() => { setTemplates(null); setDocumenten(null); setToonTemplates(false); laad(); }, [laad]);

  // Nieuw tabblad i.p.v. downloaden: een PDF bekijk je meteen, Office-bestanden
  // downloadt de browser alsnog vanzelf.
  const downloadVia = async (url) => {
    const r = await authFetch(url);
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    const d = await r.json();
    window.open(d.url, '_blank', 'noopener');
  };

  const uploadDocument = async (file) => {
    if (!file || uploading || !projectRecId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fase', fase);
      fd.append('naam', file.name.replace(/\.[^.]+$/, ''));
      if (projectNumber) fd.append('projectNumber', projectNumber);
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      toast.success('Document bewaard bij deze fase');
      await laad();
    } catch (e) { toast.error(`Document uploaden mislukt: ${e.message}`); }
    finally { setUploading(false); }
  };

  const uploadTemplate = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('naam', file.name.replace(/\.[^.]+$/, ''));
      fd.append('fase', fase);
      const r = await authFetch(`${BACKEND_URL}/Api/flow-templates`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      toast.success(d.vervangen ? 'Template vervangen door nieuwe versie' : 'Template toegevoegd aan deze fase');
      await laad();
    } catch (e) { toast.error(`Template uploaden mislukt: ${e.message}`); }
    finally { setUploading(false); }
  };

  const verwijderTemplate = async (t) => {
    if (!(await bevestig({ titel: `Template "${t.naam}" uit deze fase verwijderen?`, tekst: 'Dit geldt voor alle projecten.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    setBusyId(`tpl-${t.id}`);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/flow-templates/${t.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await laad();
      toast.success('Template verwijderd uit deze fase');
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
    finally { setBusyId(null); }
  };

  const verwijderDocument = async (doc) => {
    if (!(await bevestig({ titel: `Document "${doc.naam}" uit deze fase verwijderen?`, tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    setBusyId(`doc-${doc.id}`);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/documents/${doc.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await laad();
      toast.success('Document verwijderd uit deze fase');
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
    finally { setBusyId(null); }
  };

  const chip = (icoon, label, onClick, extra = null, key) => (
    <Box key={key} onClick={onClick} sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.9,
      borderRadius: '0.6rem', cursor: 'pointer', bgcolor: T.surfaceLow,
      fontFamily: T.font, fontSize: '0.8rem', fontWeight: 700, color: T.text,
      transition: 'all 0.15s ease', '&:hover': { bgcolor: T.surfaceHigh },
    }}>
      <Icon name={icoon} size={17} sx={{ color: T.tertiary }} />
      {label}
      {extra}
    </Box>
  );

  const laden = templates === null || documenten === null;
  const heeftDocumenten = (documenten || []).length > 0;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography sx={{ ...stitchSectionLabel }}>Documenten</Typography>
        <Box sx={{ flex: 1 }} />
        <input ref={docUploadRef} type="file" hidden onChange={(e) => { uploadDocument(e.target.files?.[0]); e.target.value = ''; }} />
        <Tooltip title="Upload het ingevulde document (concept/script/draaiboek)" slotProps={stitchTooltipSlotProps}>
          <Box component="span" onClick={() => !uploading && docUploadRef.current?.click()}
            sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem', opacity: uploading ? 0.6 : 1 }}>
            {uploading ? <CircularProgress size={14} /> : <Icon name="upload_file" size={15} />}
            Document uploaden
          </Box>
        </Tooltip>
      </Box>

      {laden ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: T.outline }}>
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>Documenten ophalen…</Typography>
        </Box>
      ) : (
        <>
          {heeftDocumenten && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {documenten.map((doc) => (
                <Tooltip key={doc.id} slotProps={stitchTooltipSlotProps}
                  title={`${doc.bestandsnaam}${doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}${doc.created_at ? ` · ${doc.created_at.slice(0, 10)}` : ''} — klik om te downloaden`}>
                  {chip(bestandsIcoon(doc.bestandsnaam), doc.naam,
                    () => busyId !== `doc-${doc.id}` && downloadVia(
                      `${BACKEND_URL}/Api/project-flow/${projectRecId}/documents/${doc.id}/download`, doc.bestandsnaam,
                    ).catch((e) => toast.error(`Download mislukt: ${e.message}`)),
                    <Icon name="close" size={13} sx={{ color: T.outlineVariant, '&:hover': { color: '#a83836' } }}
                      onClick={(e) => { e.stopPropagation(); verwijderDocument(doc); }} />,
                    doc.id)}
                </Tooltip>
              ))}
            </Box>
          )}

          {/* Templates: startpunt — standaard ingeklapt tot één regel, ze
              namen anders meer ruimte in dan de taken zelf (Joris, 25 aug) */}
          {!toonTemplates ? (
            <Typography variant="caption" onClick={() => setToonTemplates(true)}
              sx={{ fontFamily: T.fontBody, color: T.outlineVariant, cursor: 'pointer', '&:hover': { color: T.primary } }}>
              {(templates || []).length
                ? `${templates.length} template${templates.length === 1 ? '' : 's'} voor deze fase — tonen`
                : 'Nog geen templates voor deze fase — toevoegen'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {(templates || []).length > 0 && (
                <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, fontWeight: 700, letterSpacing: '0.05em', width: '100%' }}>
                  TEMPLATES (startpunt — upload het ingevulde document hierboven)
                </Typography>
              )}
              {(templates || []).map((t) => (
                <Tooltip key={`t-${t.id}`} slotProps={stitchTooltipSlotProps}
                  title={`Template · ${t.bestandsnaam}${t.categorie ? ` · ${t.categorie}` : ''} — klik om te downloaden`}>
                  {chip(bestandsIcoon(t.bestandsnaam), t.naam,
                    () => downloadVia(`${BACKEND_URL}/Api/flow-templates/${t.id}/download`, t.bestandsnaam)
                      .catch((e) => toast.error(`Download mislukt: ${e.message}`)),
                    <>
                      <Icon name="download" size={14} sx={{ color: T.outlineVariant }} />
                      <Icon name="close" size={13} sx={{ color: T.outlineVariant, '&:hover': { color: '#a83836' } }}
                        onClick={(e) => { e.stopPropagation(); busyId !== `tpl-${t.id}` && verwijderTemplate(t); }} />
                    </>, `t-${t.id}`)}
                </Tooltip>
              ))}
              <input ref={templateUploadRef} type="file" hidden onChange={(e) => { uploadTemplate(e.target.files?.[0]); e.target.value = ''; }} />
              <Tooltip title="Template toevoegen (zelfde naam = nieuwe versie)" slotProps={stitchTooltipSlotProps}>
                <Box component="span" onClick={() => !uploading && templateUploadRef.current?.click()}
                  sx={{ ...stitchOutlineBtn, py: 0.35, px: 1, fontSize: '0.72rem', opacity: uploading ? 0.6 : 1 }}>
                  <Icon name="add" size={14} />
                </Box>
              </Tooltip>
              <Typography variant="caption" onClick={() => setToonTemplates(false)}
                sx={{ fontFamily: T.fontBody, color: T.outlineVariant, cursor: 'pointer', '&:hover': { color: T.primary } }}>
                verbergen
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default FaseTemplates;
